import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Pagination } from 'react-bootstrap';
import { toString } from 'lodash';

import { formatedStringCounters } from '../../Pagination/helpers';

import { useFilters } from '../../../providers/Filters/useFilters';
import { Flex } from '../../Layout';
import Dropdown from '../../Dropdown/Default';
import Spinner from '../../Loaders/Spinner';

const FilterPagination = ({
  pages,
  currentPage: propCurrentPage,
  itemsCount,
  pageSize,
  pagesLimit,
  preLoader,
  pageSizes,
  showPageSize,
  filterMethod,
}) => {
  const { filters, onChangeFilter } = useFilters();
  const filter = filters[filterMethod]

  const currentPage = propCurrentPage || Number(filter.page) || 1;
  const totalPages = pages || Math.ceil(itemsCount / pageSize);

  const pageSizeTitle = useMemo(() => {
    const size = filter.page_size || pageSizes?.[0].code;
    if (size) {
      return toString(size)
    }
    return '100'
  }, [filter.page_size, pageSizes])

  const onChangePage = (newPage) => {
    if (preLoader) return;
    if (newPage < 1 || newPage > totalPages) return;

    onChangeFilter({
      [filterMethod]: {
        page: newPage,
        page_size: filter?.page_size || null,
      },
    })
  };

  const onChangePageSize = (value) => {
    if (Number(value) !== Number(filter.page_size)) {
      onChangeFilter({
        query: {
          page: null,
          page_size: value === pageSizes?.[0].code ? null : value,
        },
      })
    }
  }

  const generatePages = () => {
    if (!totalPages) return [];
    let startPage = Math.max(1, currentPage - Math.floor(pagesLimit / 2));
    const endPage = Math.min(startPage + pagesLimit - 1, totalPages);

    if (endPage - startPage < pagesLimit - 1) {
      startPage = Math.max(1, endPage - pagesLimit + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  if (preLoader) {
    return (
      <div className="pagination">
        <Spinner />
      </div>
    );
  }

  return (
    <Flex
      gap={16}
      justify={{ xs: 'between' }}
      direction={{ default: 'column', xs: 'row' }}
      alignItems="center"
      style={{ paddingTop: '15px', paddingBottom: '15px' }}
    >
      {totalPages <= 1 ? <div /> : (
        <Pagination className="mb-0 mt-0 w-100">
          {currentPage > 1 && (
            <Pagination.First
              onClick={() => onChangePage(currentPage - 1)}
            />
          )}

          {generatePages().map((page) => (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => onChangePage(page)}
            >
              {page}
            </Pagination.Item>
          ))}

          {currentPage < totalPages && (
            <Pagination.Last
              onClick={() => onChangePage(currentPage + 1)}
            />
          )}
        </Pagination>
      )}
      {itemsCount > 0 && showPageSize
        ? (
          <Flex align="center" wrap={{ default: 'wrap', xs: 'nowrap' }} className="nowrap" gap={4}>
            {formatedStringCounters(currentPage, pageSize, itemsCount)}
            {'.'}
            <Flex align="center" gap={4}>
              Records per page
              {' '}
              <Dropdown
                handleSelect={onChangePageSize}
                title={pageSizeTitle}
                items={pageSizes}
                simple
                dropUp
                size="xsmall"
              />
            </Flex>
          </Flex>
        ) : null}
    </Flex>
  );
};

FilterPagination.propTypes = {
  pages: PropTypes.number,
  currentPage: PropTypes.number,
  itemsCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageSizes: PropTypes.array,
  pagesLimit: PropTypes.number,
  preLoader: PropTypes.bool,
  showPageSize: PropTypes.bool,
  filterMethod: PropTypes.string,
};

FilterPagination.defaultProps = {
  filterMethod: 'query',
  pagesLimit: 5,
  preLoader: false,
  showPageSize: true,
  pageSizes: [
    { title: '100', code: 100 },
    { title: '200', code: 200 },
    { title: '500', code: 500 },
    { title: '1000', code: 1000 },
  ],
};

export default memo(FilterPagination);
